/** Кривая скорости пролёта. Время секций фиксировано, доля пути — интеграл кривой. */

export const MAX_SPEED_SEGMENTS = 32
export const MAX_SPEED_KNOTS = 16
export const MIN_SPEED_CUT_GAP_MS = 200
/** Верх графика. Ровный пресет сидит на середине; выше неё проезд быстрее ровного. */
export const SPEED_Y_MAX = 4
/** Внутренняя точка не подходит к краю ближе этого зазора и не подменяет край. */
const SPEED_EDGE_GAP = 0.03
/** Точки группы не слипаются и не проходят сквозь точку вне группы. */
const SPEED_NEIGHBOR_GAP = 1e-3

export const SPEED_PRESETS = ["uniform", "accel", "brake", "smooth", "pause"] as const

export type SpeedPresetName = (typeof SPEED_PRESETS)[number]

export type SpeedPreset = SpeedPresetName | "custom"

export type SpeedKnot = { u: number; y: number }

export type SpeedSegment = {
  preset: SpeedPreset
  /** 0..1. Масштаб формы пресета. У своей кривой высота живёт в точках. */
  gain: number
  knots: SpeedKnot[]
}

export type CameraSpeedProfile = {
  cuts: number[]
  segments: SpeedSegment[]
}

export type SpeedSectionStats = {
  index: number
  t0: number
  t1: number
  segment: SpeedSegment
  timeShare: number
  pathShare: number
  multiplier: number | null
}

const PRESET_SET = new Set<SpeedPreset>(["uniform", "accel", "brake", "smooth", "pause", "custom"])

function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v))
}

function clamp01(v: number) {
  return clamp(v, 0, 1)
}

function clampSpeedY(v: number) {
  return clamp(v, 0, SPEED_Y_MAX)
}

function smoothstep(u: number) {
  const t = clamp01(u)
  return t * t * (3 - 2 * t)
}

export function uniformSpeedSegment(gain = 1): SpeedSegment {
  return { preset: "uniform", gain: clamp01(gain), knots: [] }
}

export function presetSpeedSegment(preset: SpeedPresetName): SpeedSegment {
  if (preset === "pause") return { preset: "pause", gain: 0, knots: [] }
  const mid = SPEED_Y_MAX / 2
  const edges: [number, number] =
    preset === "accel" ? [0, mid] : preset === "brake" ? [mid, 0] : preset === "smooth" ? [0, 0] : [mid, mid]
  return {
    preset,
    gain: 1,
    knots: [
      { u: 0, y: edges[0] },
      { u: 1, y: edges[1] },
    ],
  }
}

function withUniformEdgeKnots(segment: SpeedSegment): SpeedSegment {
  if (segment.preset !== "uniform") return segment
  const hasLeft = segment.knots.some((knot) => knot.u <= 1e-4)
  const hasRight = segment.knots.some((knot) => knot.u >= 1 - 1e-4)
  if (hasLeft && hasRight) return segment
  const knots = segment.knots.map((knot) => ({ u: knot.u, y: knot.y }))
  if (!hasLeft) knots.unshift({ u: 0, y: segmentSpeedAt(segment, 0) })
  if (!hasRight) knots.push({ u: 1, y: segmentSpeedAt(segment, 1) })
  return { preset: segment.preset, gain: segment.gain, knots }
}

const DEFAULT_FLY_END_SHARE = 0.15

/** Новый пролёт: 15% разгон, длинная середина ровно, 15% торможение. Короткий ролик — одна ровная секция. */
export function defaultFlySpeedProfile(durationMs: number): CameraSpeedProfile {
  const duration = Math.max(0, Math.round(durationMs))
  const end = Math.round(duration * DEFAULT_FLY_END_SHARE)
  if (end < MIN_SPEED_CUT_GAP_MS || duration - end * 2 < MIN_SPEED_CUT_GAP_MS) {
    return { cuts: [], segments: [presetSpeedSegment("uniform")] }
  }
  return {
    cuts: [end, duration - end],
    segments: [presetSpeedSegment("accel"), presetSpeedSegment("uniform"), presetSpeedSegment("brake")],
  }
}

export function displaySpeedProfile(speed: CameraSpeedProfile | null | undefined): CameraSpeedProfile {
  if (!speed || speed.segments.length === 0) {
    return { cuts: [], segments: [presetSpeedSegment("uniform")] }
  }
  let changed = false
  const segments = speed.segments.map((segment) => {
    const next = withUniformEdgeKnots(segment)
    if (next !== segment) changed = true
    return next
  })
  return changed ? { cuts: speed.cuts, segments } : speed
}

export function cloneSpeedSegment(segment: SpeedSegment): SpeedSegment {
  return {
    preset: segment.preset,
    gain: segment.gain,
    knots: segment.knots.map((knot) => ({ u: knot.u, y: knot.y })),
  }
}

export function cloneSpeedProfile(profile: CameraSpeedProfile): CameraSpeedProfile {
  return {
    cuts: [...profile.cuts],
    segments: profile.segments.map(cloneSpeedSegment),
  }
}

function isSpeedEdgeU(u: number) {
  return u <= 1e-4 || u >= 1 - 1e-4
}

function dedupedKnots(knots: readonly SpeedKnot[]): SpeedKnot[] {
  const sorted = knots
    .filter((knot) => Number.isFinite(knot.u) && Number.isFinite(knot.y))
    .map((knot) => ({ u: clamp01(knot.u), y: clampSpeedY(knot.y) }))
    .sort((a, b) => a.u - b.u)
  const dedup: SpeedKnot[] = []
  for (const knot of sorted) {
    const prev = dedup[dedup.length - 1]
    if (prev && Math.abs(prev.u - knot.u) < 1e-4) dedup[dedup.length - 1] = knot
    else dedup.push(knot)
  }
  return dedup
}

/** Края u=0 и u=1. Высота недостающего края — высота ближайшего узла. */
function ensureEdgeKnots(knots: readonly SpeedKnot[]): SpeedKnot[] {
  const dedup = dedupedKnots(knots)
  if (dedup.length === 0) return dedup
  if (isSpeedEdgeU(dedup[0].u)) dedup[0] = { u: 0, y: dedup[0].y }
  else dedup.unshift({ u: 0, y: dedup[0].y })
  const last = dedup[dedup.length - 1]
  if (isSpeedEdgeU(last.u)) dedup[dedup.length - 1] = { u: 1, y: last.y }
  else dedup.push({ u: 1, y: last.y })
  while (dedup.length > MAX_SPEED_KNOTS && dedup.length > 2) dedup.splice(1, 1)
  return dedup
}

function storedKnots(segment: SpeedSegment): SpeedKnot[] {
  return dedupedKnots(segment.knots)
}

function customPoints(segment: SpeedSegment): SpeedKnot[] {
  const dedup = storedKnots(segment)
  if (dedup.length === 0) {
    const y = presetUnit(segment.gain)
    return [
      { u: 0, y },
      { u: 1, y },
    ]
  }
  const pts: SpeedKnot[] = []
  if (dedup[0].u > 0) pts.push({ u: 0, y: dedup[0].y })
  pts.push(...dedup)
  const last = dedup[dedup.length - 1]
  if (last.u < 1) pts.push({ u: 1, y: last.y })
  return pts
}

/** Точки штриха своей кривой, вместе с краями. null — пресет или пустая кривая. */
export function speedStrokePoints(segment: SpeedSegment): SpeedKnot[] | null {
  if (segment.preset !== "custom") return null
  const knots = ensureEdgeKnots(storedKnots(segment))
  if (knots.length === 0) return null
  return knots
}

function sampleLinear(pts: readonly SpeedKnot[], u: number): number {
  const t = clamp01(u)
  if (pts.length === 0) return 0
  if (t <= pts[0].u) return pts[0].y
  const last = pts[pts.length - 1]
  if (t >= last.u) return last.y
  let i = 1
  while (i < pts.length && pts[i].u < t) i += 1
  const a = pts[i - 1]
  const b = pts[i]
  const span = b.u - a.u
  if (span <= 1e-8) return a.y
  return a.y + (b.y - a.y) * ((t - a.u) / span)
}

/** Полная единица пресета: середина графика. `gain` остаётся 0…1. */
function presetUnit(gain: number) {
  return clamp01(gain) * (SPEED_Y_MAX / 2)
}

/** Края пресета. Нет узла — неявная высота формы. Горб плавной кривой доходит до середины при `gain` 1. */
function presetEnds(segment: SpeedSegment): { y0: number; y1: number; hump: number } {
  const unit = presetUnit(segment.gain)
  const knots = storedKnots(segment)
  let left: SpeedKnot | undefined
  let right: SpeedKnot | undefined
  for (const knot of knots) {
    if (knot.u <= 1e-4) left = knot
    if (knot.u >= 1 - 1e-4) right = knot
  }
  if (segment.preset === "accel") return { y0: left?.y ?? 0, y1: right?.y ?? unit, hump: 0 }
  if (segment.preset === "brake") return { y0: left?.y ?? unit, y1: right?.y ?? 0, hump: 0 }
  if (segment.preset === "smooth") return { y0: left?.y ?? 0, y1: right?.y ?? 0, hump: unit }
  return { y0: left?.y ?? unit, y1: right?.y ?? unit, hump: 0 }
}

/** Скорость в локальной координате секции. У своей кривой высота узла доходит до верха графика. */
export function segmentSpeedAt(segment: SpeedSegment, u: number): number {
  const t = clamp01(u)
  if (segment.preset === "custom") return sampleLinear(customPoints(segment), t)
  if (segment.preset === "pause") return 0
  const { y0, y1, hump } = presetEnds(segment)
  const shape = segment.preset === "accel" || segment.preset === "brake" ? smoothstep(t) : t
  const wave = segment.preset === "smooth" ? hump * Math.sin(Math.PI * t) : 0
  return clampSpeedY(y0 + (y1 - y0) * shape + wave)
}

function integrateLinear(pts: readonly SpeedKnot[], u0: number, u1: number): number {
  const from = clamp01(Math.min(u0, u1))
  const to = clamp01(Math.max(u0, u1))
  if (to <= from) return 0
  let acc = 0
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i]
    const b = pts[i + 1]
    const left = Math.max(from, a.u)
    const right = Math.min(to, b.u)
    if (right <= left) continue
    const span = b.u - a.u
    const yLeft = span <= 1e-8 ? a.y : a.y + ((b.y - a.y) * (left - a.u)) / span
    const yRight = span <= 1e-8 ? b.y : a.y + ((b.y - a.y) * (right - a.u)) / span
    acc += (right - left) * (yLeft + yRight) * 0.5
  }
  return acc
}

function presetAntideriv(segment: SpeedSegment, u: number): number {
  const t = clamp01(u)
  if (segment.preset === "pause" || segment.preset === "custom") return 0
  const { y0, y1, hump } = presetEnds(segment)
  const rise = y1 - y0
  if (segment.preset === "accel" || segment.preset === "brake") {
    const ramp = t ** 3 - 0.5 * t ** 4
    return y0 * t + rise * ramp
  }
  const line = y0 * t + rise * t * t * 0.5
  if (segment.preset === "smooth") return line + (hump * (1 - Math.cos(Math.PI * t))) / Math.PI
  return line
}

function integrateClampedPreset(segment: SpeedSegment, u0: number, u1: number): number {
  const from = clamp01(Math.min(u0, u1))
  const to = clamp01(Math.max(u0, u1))
  if (to <= from) return 0
  const steps = 32
  const du = (to - from) / steps
  let acc = 0
  let prev = segmentSpeedAt(segment, from)
  for (let i = 1; i <= steps; i += 1) {
    const y = segmentSpeedAt(segment, from + du * i)
    acc += (prev + y) * 0.5 * du
    prev = y
  }
  return acc
}

function segmentIntegral(segment: SpeedSegment, u0: number, u1: number): number {
  if (segment.preset === "custom") return integrateLinear(customPoints(segment), u0, u1)
  if (segment.preset === "smooth") {
    const { y0, y1, hump } = presetEnds(segment)
    if (Math.max(y0, y1) + hump > SPEED_Y_MAX) return integrateClampedPreset(segment, u0, u1)
  }
  return presetAntideriv(segment, u1) - presetAntideriv(segment, u0)
}

function edgesOf(profile: CameraSpeedProfile, durationMs: number): number[] {
  const duration = Math.max(0, durationMs)
  const cuts = profile.cuts.filter((cut) => cut > 0 && cut < duration)
  return [0, ...cuts, duration]
}

function integralUntil(profile: CameraSpeedProfile, durationMs: number, t: number): number {
  const duration = Math.max(0, durationMs)
  const end = clamp(t, 0, duration)
  const edges = edgesOf(profile, duration)
  let acc = 0
  const count = Math.min(profile.segments.length, edges.length - 1)
  for (let i = 0; i < count; i += 1) {
    const t0 = edges[i]
    const t1 = edges[i + 1]
    const dt = t1 - t0
    if (dt <= 0 || end <= t0) continue
    const u1 = end >= t1 ? 1 : (end - t0) / dt
    acc += dt * segmentIntegral(profile.segments[i], 0, u1)
  }
  return acc
}

/** Доля длины траектории к моменту t. Вся кривая на нуле — 0, иначе к концу ролика 1. */
export function pathProgress(profile: CameraSpeedProfile, durationMs: number, t: number): number {
  const total = integralUntil(profile, durationMs, durationMs)
  if (total <= 1e-8) return 0
  return Math.min(1, integralUntil(profile, durationMs, t) / total)
}

/** Часы, когда камера доезжает до доли дуги. Без графика доля равна доле часов. */
export function clockAtArcFraction(
  speed: CameraSpeedProfile | null | undefined,
  durationMs: number,
  fraction: number,
): number {
  const duration = Math.max(0, durationMs)
  const target = clamp(fraction, 0, 1)
  if (!speed) return target * duration
  if (target <= 0) return 0
  if (target >= 1) return duration
  let low = 0
  let high = duration
  for (let step = 0; step < 24; step += 1) {
    const mid = (low + high) / 2
    if (pathProgress(speed, duration, mid) < target) low = mid
    else high = mid
  }
  return high
}

export function speedSectionStats(profile: CameraSpeedProfile, durationMs: number): SpeedSectionStats[] {
  const duration = Math.max(0, durationMs)
  const edges = edgesOf(profile, duration)
  const weights: number[] = []
  const count = Math.min(profile.segments.length, Math.max(0, edges.length - 1))
  for (let i = 0; i < count; i += 1) {
    const dt = edges[i + 1] - edges[i]
    weights.push(dt > 0 ? dt * segmentIntegral(profile.segments[i], 0, 1) : 0)
  }
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  return profile.segments.slice(0, count).map((segment, index) => {
    const t0 = edges[index] ?? 0
    const t1 = edges[index + 1] ?? duration
    const timeShare = duration > 0 ? (t1 - t0) / duration : 0
    const pathShare = total > 1e-8 ? weights[index] / total : 0
    const multiplier = segment.preset === "pause" || timeShare < 1e-6 || pathShare < 1e-4 ? null : pathShare / timeShare
    return { index, t0, t1, segment, timeShare, pathShare, multiplier }
  })
}

/** Проценты долей пути. Сумма 100, когда путь вообще есть. */
export function pathSharePercents(shares: readonly number[]): number[] {
  const total = shares.reduce((sum, share) => sum + share, 0)
  if (total <= 1e-8) return shares.map(() => 0)
  const raw = shares.map((share) => (share / total) * 100)
  const floors = raw.map((value) => Math.floor(value))
  let left = 100 - floors.reduce((sum, value) => sum + value, 0)
  const order = raw.map((value, index) => ({ index, frac: value - Math.floor(value) })).sort((a, b) => b.frac - a.frac)
  const out = floors.slice()
  for (let i = 0; i < order.length && left > 0; i += 1) {
    out[order[i].index] += 1
    left -= 1
  }
  return out
}

export function curveSamples(segment: SpeedSegment, count = 32): SpeedKnot[] {
  const n = Math.max(2, count)
  const out: SpeedKnot[] = []
  for (let i = 0; i < n; i += 1) {
    const u = i / (n - 1)
    out.push({ u, y: segmentSpeedAt(segment, u) })
  }
  return out
}

function replaceSegment(profile: CameraSpeedProfile, index: number, segment: SpeedSegment): CameraSpeedProfile {
  return {
    cuts: [...profile.cuts],
    segments: profile.segments.map((item, i) => (i === index ? segment : cloneSpeedSegment(item))),
  }
}

function resample(segment: SpeedSegment, u0: number, u1: number, count = 8): SpeedSegment {
  const knots: SpeedKnot[] = []
  const n = Math.max(1, count - 1)
  for (let i = 0; i <= n; i += 1) {
    const local = i / n
    const u = u0 + (u1 - u0) * local
    knots.push({ u: local, y: clampSpeedY(segmentSpeedAt(segment, u)) })
  }
  return { preset: "custom", gain: 1, knots }
}

function splitSegment(segment: SpeedSegment, u: number): [SpeedSegment, SpeedSegment] {
  if (segment.preset !== "custom") return [cloneSpeedSegment(segment), cloneSpeedSegment(segment)]
  const cut = clamp(u, 1e-4, 1 - 1e-4)
  const knots = ensureEdgeKnots(segment.knots)
  const left: SpeedKnot[] = []
  const right: SpeedKnot[] = []
  let onCut = false
  for (const knot of knots) {
    if (Math.abs(knot.u - cut) <= 1e-4) {
      onCut = true
      left.push({ u: 1, y: knot.y })
      right.push({ u: 0, y: knot.y })
    } else if (knot.u < cut) left.push({ u: knot.u / cut, y: knot.y })
    else right.push({ u: (knot.u - cut) / (1 - cut), y: knot.y })
  }
  if (!onCut) {
    const yCut = segmentSpeedAt(segment, cut)
    left.push({ u: 1, y: yCut })
    right.unshift({ u: 0, y: yCut })
  }
  return [
    { preset: "custom", gain: 1, knots: ensureEdgeKnots(left) },
    { preset: "custom", gain: 1, knots: ensureEdgeKnots(right) },
  ]
}

function joinedCustomKnots(a: SpeedSegment, b: SpeedSegment, dtA: number, dtB: number): SpeedKnot[] {
  const total = dtA + dtB
  const left = a.preset === "custom" ? ensureEdgeKnots(a.knots).filter((knot) => knot.u < 1 - 1e-4) : []
  const right = b.preset === "custom" ? ensureEdgeKnots(b.knots).filter((knot) => knot.u > 1e-4) : []
  const joinU = dtA / total
  const knots: SpeedKnot[] = [
    ...left.map((knot) => ({ u: (knot.u * dtA) / total, y: knot.y })),
    { u: joinU, y: clampSpeedY((segmentSpeedAt(a, 1) + segmentSpeedAt(b, 0)) / 2) },
    ...right.map((knot) => ({ u: (dtA + knot.u * dtB) / total, y: knot.y })),
  ]
  while (knots.length > MAX_SPEED_KNOTS) {
    const drop = knots.findIndex((knot) => !isSpeedEdgeU(knot.u) && Math.abs(knot.u - joinU) > 1e-4)
    if (drop < 0) break
    knots.splice(drop, 1)
  }
  return ensureEdgeKnots(knots)
}

function mergeSegmentPair(a: SpeedSegment, b: SpeedSegment, dtA: number, dtB: number): SpeedSegment {
  if (a.preset === "pause" && b.preset === "pause") return presetSpeedSegment("pause")
  if (a.preset === "uniform" && b.preset === "uniform" && Math.abs(a.gain - b.gain) < 1e-3) {
    return cloneSpeedSegment(a)
  }
  const total = dtA + dtB
  if (total <= 1e-6) return cloneSpeedSegment(a)
  if (a.preset === "custom" || b.preset === "custom") {
    return { preset: "custom", gain: 1, knots: joinedCustomKnots(a, b, dtA, dtB) }
  }
  const knots: SpeedKnot[] = []
  const n = 8
  for (let i = 0; i <= n; i += 1) {
    const time = (i / n) * total
    const y =
      time <= dtA
        ? segmentSpeedAt(a, dtA <= 1e-6 ? 0 : time / dtA)
        : segmentSpeedAt(b, dtB <= 1e-6 ? 0 : (time - dtA) / dtB)
    knots.push({ u: i / n, y: clampSpeedY(y) })
  }
  return { preset: "custom", gain: 1, knots }
}

function normalizeSegment(raw: unknown): SpeedSegment | null {
  if (!raw || typeof raw !== "object") return null
  const body = raw as { preset?: unknown; gain?: unknown; knots?: unknown }
  const preset = body.preset
  if (typeof preset !== "string" || !PRESET_SET.has(preset as SpeedPreset)) return null
  const gainRaw = typeof body.gain === "number" && Number.isFinite(body.gain) ? body.gain : 1
  const knots: SpeedKnot[] = []
  if (Array.isArray(body.knots)) {
    for (const item of body.knots) {
      if (knots.length >= MAX_SPEED_KNOTS) break
      if (!item || typeof item !== "object") continue
      const knot = item as { u?: unknown; y?: unknown }
      if (typeof knot.u !== "number" || typeof knot.y !== "number") continue
      if (!Number.isFinite(knot.u) || !Number.isFinite(knot.y)) continue
      knots.push({ u: clamp01(knot.u), y: clampSpeedY(knot.y) })
    }
  }
  if (preset === "pause") return { preset: "pause", gain: 0, knots: [] }
  const kept = preset === "custom" ? ensureEdgeKnots(knots) : knots
  return { preset: preset as SpeedPreset, gain: clamp01(gainRaw), knots: kept }
}

function repairSpeedProfile(profile: CameraSpeedProfile, durationMs: number): CameraSpeedProfile | null {
  const duration = durationMs
  if (!(duration > 0) || profile.segments.length === 0) return null
  let segments = profile.segments.map(cloneSpeedSegment)
  let cuts = profile.cuts.map((cut) => Math.round(cut)).filter((cut) => cut > 0 && cut < duration)
  cuts.sort((a, b) => a - b)
  const unique: number[] = []
  for (const cut of cuts) {
    const prev = unique[unique.length - 1]
    if (prev != null && cut - prev < 1) continue
    unique.push(cut)
  }
  cuts = unique
  while (segments.length < cuts.length + 1) segments.push(uniformSpeedSegment())
  if (segments.length > cuts.length + 1) segments = segments.slice(0, cuts.length + 1)
  if (segments.length > MAX_SPEED_SEGMENTS) {
    segments = segments.slice(0, MAX_SPEED_SEGMENTS)
    cuts = cuts.slice(0, MAX_SPEED_SEGMENTS - 1)
  }

  if (duration < MIN_SPEED_CUT_GAP_MS * 2) {
    return { cuts: [], segments: [segments[0] ?? uniformSpeedSegment()] }
  }

  let guard = 0
  while (guard < MAX_SPEED_SEGMENTS) {
    guard += 1
    const edges = [0, ...cuts, duration]
    let short = -1
    for (let i = 0; i < edges.length - 1; i += 1) {
      if (edges[i + 1] - edges[i] < MIN_SPEED_CUT_GAP_MS - 1e-6) {
        short = i
        break
      }
    }
    if (short < 0 || segments.length <= 1) break
    const neighbor = short === segments.length - 1 ? short - 1 : short
    const left = neighbor
    const dtA = edges[left + 1] - edges[left]
    const dtB = edges[left + 2] - edges[left + 1]
    const merged = mergeSegmentPair(segments[left], segments[left + 1], dtA, dtB)
    segments = [...segments.slice(0, left), merged, ...segments.slice(left + 2)]
    cuts = cuts.filter((_, index) => index !== left)
  }
  if (segments.length === 0) return null
  return { cuts, segments }
}

export function normalizeSpeedProfile(raw: unknown, durationMs: number): CameraSpeedProfile | null {
  if (!raw || typeof raw !== "object" || !(durationMs > 0)) return null
  const body = raw as { cuts?: unknown; segments?: unknown }
  const segmentsRaw = Array.isArray(body.segments) ? body.segments : []
  const segments: SpeedSegment[] = []
  for (const item of segmentsRaw) {
    if (segments.length >= MAX_SPEED_SEGMENTS) break
    const segment = normalizeSegment(item)
    if (segment) segments.push(segment)
  }
  if (segments.length === 0) return null
  const cuts: number[] = []
  if (Array.isArray(body.cuts)) {
    for (const item of body.cuts) {
      if (typeof item !== "number" || !Number.isFinite(item)) continue
      cuts.push(item)
    }
  }
  const alignedCuts = cuts.slice(0, Math.max(0, segments.length - 1))
  return repairSpeedProfile({ cuts: alignedCuts, segments }, durationMs)
}

export function splitSpeedProfile(
  profile: CameraSpeedProfile,
  durationMs: number,
  tMs: number,
): CameraSpeedProfile | null {
  const duration = durationMs
  if (!(duration > MIN_SPEED_CUT_GAP_MS * 2)) return null
  if (profile.segments.length >= MAX_SPEED_SEGMENTS) return null
  const t = Math.round(tMs)
  const edges = edgesOf(profile, duration)
  for (const edge of edges) {
    if (Math.abs(edge - t) < MIN_SPEED_CUT_GAP_MS) return null
  }
  let index = -1
  for (let i = 0; i < edges.length - 1; i += 1) {
    if (t > edges[i] && t < edges[i + 1]) {
      index = i
      break
    }
  }
  if (index < 0 || index >= profile.segments.length) return null
  const t0 = edges[index]
  const t1 = edges[index + 1]
  if (t - t0 < MIN_SPEED_CUT_GAP_MS || t1 - t < MIN_SPEED_CUT_GAP_MS) return null
  const u = (t - t0) / (t1 - t0)
  const [left, right] = splitSegment(profile.segments[index], u)
  return {
    cuts: [...profile.cuts.slice(0, index), t, ...profile.cuts.slice(index)],
    segments: [...profile.segments.slice(0, index), left, right, ...profile.segments.slice(index + 1)].map(
      cloneSpeedSegment,
    ),
  }
}

export function moveSpeedCut(
  profile: CameraSpeedProfile,
  durationMs: number,
  cutIndex: number,
  tMs: number,
): CameraSpeedProfile {
  if (cutIndex < 0 || cutIndex >= profile.cuts.length) return profile
  const prev = cutIndex === 0 ? 0 : profile.cuts[cutIndex - 1]
  const next = cutIndex === profile.cuts.length - 1 ? durationMs : profile.cuts[cutIndex + 1]
  if (next - prev < MIN_SPEED_CUT_GAP_MS * 2) return profile
  const t = Math.round(clamp(tMs, prev + MIN_SPEED_CUT_GAP_MS, next - MIN_SPEED_CUT_GAP_MS))
  const cuts = profile.cuts.slice()
  cuts[cutIndex] = t
  return { cuts, segments: profile.segments.map(cloneSpeedSegment) }
}

export function removeSpeedCut(profile: CameraSpeedProfile, durationMs: number, cutIndex: number): CameraSpeedProfile {
  if (cutIndex < 0 || cutIndex >= profile.cuts.length) return profile
  const edges = edgesOf(profile, durationMs)
  const left = cutIndex
  const dtA = edges[left + 1] - edges[left]
  const dtB = edges[left + 2] - edges[left + 1]
  const merged = mergeSegmentPair(profile.segments[left], profile.segments[left + 1], dtA, dtB)
  return {
    cuts: profile.cuts.filter((_, index) => index !== cutIndex),
    segments: [...profile.segments.slice(0, left), merged, ...profile.segments.slice(left + 2)].map(cloneSpeedSegment),
  }
}

export function applySpeedPreset(
  profile: CameraSpeedProfile,
  index: number,
  preset: SpeedPresetName,
): CameraSpeedProfile {
  if (index < 0 || index >= profile.segments.length) return profile
  return replaceSegment(profile, index, presetSpeedSegment(preset))
}

export function setSpeedGain(profile: CameraSpeedProfile, index: number, gain: number): CameraSpeedProfile {
  const segment = profile.segments[index]
  if (!segment || segment.preset === "custom" || segment.preset === "pause") return profile
  return replaceSegment(profile, index, { ...cloneSpeedSegment(segment), gain: clamp01(gain) })
}

export function addSpeedKnot(
  profile: CameraSpeedProfile,
  index: number,
  u: number,
  y: number,
): CameraSpeedProfile | null {
  const segment = profile.segments[index]
  if (!segment) return null
  const uu = clamp01(u)
  const yy = clampSpeedY(y)
  const knots =
    segment.preset === "custom"
      ? ensureEdgeKnots(segment.knots)
      : [
          { u: 0, y: segmentSpeedAt(segment, 0) },
          { u: 1, y: segmentSpeedAt(segment, 1) },
        ]
  const near = knots.findIndex((knot) => Math.abs(knot.u - uu) < SPEED_EDGE_GAP)
  if (near >= 0) {
    const edge = isSpeedEdgeU(knots[near].u)
    knots[near] = { u: edge ? knots[near].u : clamp(uu, SPEED_EDGE_GAP, 1 - SPEED_EDGE_GAP), y: yy }
  } else if (knots.length >= MAX_SPEED_KNOTS) return null
  else knots.push({ u: clamp(uu, SPEED_EDGE_GAP, 1 - SPEED_EDGE_GAP), y: yy })
  return replaceSegment(profile, index, { preset: "custom", gain: 1, knots: ensureEdgeKnots(knots) })
}

export function moveSpeedKnot(
  profile: CameraSpeedProfile,
  segmentIndex: number,
  knotIndex: number,
  u: number,
  y: number,
): { profile: CameraSpeedProfile; knotIndex: number } | null {
  const segment = profile.segments[segmentIndex]
  if (!segment) return null
  if (segment.preset !== "custom") {
    if (segment.preset === "pause") return null
    if (knotIndex < 0 || knotIndex >= segment.knots.length) return null
    const current = segment.knots[knotIndex]
    if (!isSpeedEdgeU(current.u)) return null
    const nextU = current.u < 0.5 ? 0 : 1
    const knots = segment.knots.map((knot, index) =>
      index === knotIndex ? { u: nextU, y: clampSpeedY(y) } : { u: knot.u, y: knot.y },
    )
    return {
      profile: replaceSegment(profile, segmentIndex, { preset: segment.preset, gain: segment.gain, knots }),
      knotIndex,
    }
  }
  if (knotIndex < 0 || knotIndex >= segment.knots.length) return null
  const current = segment.knots[knotIndex]
  const edge = isSpeedEdgeU(current.u)
  const nextU = edge ? (current.u < 0.5 ? 0 : 1) : clamp(clamp01(u), SPEED_EDGE_GAP, 1 - SPEED_EDGE_GAP)
  const knots = segment.knots.map((knot, index) =>
    index === knotIndex ? { u: nextU, y: clampSpeedY(y) } : { u: knot.u, y: knot.y },
  )
  const ensured = ensureEdgeKnots(knots)
  const nextIndex = ensured.findIndex(
    (knot) => Math.abs(knot.u - nextU) < 1e-4 && Math.abs(knot.y - clampSpeedY(y)) < 1e-4,
  )
  return {
    profile: replaceSegment(profile, segmentIndex, { preset: "custom", gain: 1, knots: ensured }),
    knotIndex: nextIndex < 0 ? knotIndex : nextIndex,
  }
}

export type SpeedKnotMove = { segment: number; knot: number; u: number; y: number }

export type SpeedKnotRef = { segment: number; knot: number }

/**
 * Сдвигает несколько точек за один проход. Индексы в `moves` относятся к исходному профилю.
 * Край и край пресета меняют только высоту. Пауза не двигается. Внутренняя точка не
 * переходит через разрез и не проходит сквозь точку, которой нет в этом сдвиге.
 */
export function moveSpeedKnots(
  profile: CameraSpeedProfile,
  moves: readonly SpeedKnotMove[],
): { profile: CameraSpeedProfile; knots: SpeedKnotRef[] } | null {
  if (moves.length === 0) return null
  const bySegment = new Map<number, SpeedKnotMove[]>()
  for (const move of moves) {
    const list = bySegment.get(move.segment)
    if (list) list.push(move)
    else bySegment.set(move.segment, [move])
  }

  let next = profile
  const placed = new Map<SpeedKnotMove, { u: number; y: number }>()
  for (const [segmentIndex, segmentMoves] of bySegment) {
    const segment = next.segments[segmentIndex]
    if (!segment) continue
    const result = moveSegmentKnots(segment, segmentMoves)
    if (!result) continue
    next = replaceSegment(next, segmentIndex, result.segment)
    for (const [move, pos] of result.placed) placed.set(move, pos)
  }

  const knots = moves.map((move) => {
    const pos = placed.get(move)
    const segment = next.segments[move.segment]
    if (!pos || !segment) return { segment: move.segment, knot: move.knot }
    const knotIndex = segment.knots.findIndex(
      (knot) => Math.abs(knot.u - pos.u) < 1e-4 && Math.abs(knot.y - pos.y) < 1e-4,
    )
    return { segment: move.segment, knot: knotIndex < 0 ? move.knot : knotIndex }
  })
  return { profile: next, knots }
}

function moveSegmentKnots(
  segment: SpeedSegment,
  moves: readonly SpeedKnotMove[],
): { segment: SpeedSegment; placed: Map<SpeedKnotMove, { u: number; y: number }> } | null {
  if (segment.preset === "pause") return null
  const placed = new Map<SpeedKnotMove, { u: number; y: number }>()
  if (segment.preset !== "custom") {
    const knots = segment.knots.map((knot) => ({ u: knot.u, y: knot.y }))
    let changed = false
    for (const move of moves) {
      const current = knots[move.knot]
      if (!current || !isSpeedEdgeU(current.u)) continue
      const nextU = current.u < 0.5 ? 0 : 1
      const nextY = clampSpeedY(move.y)
      knots[move.knot] = { u: nextU, y: nextY }
      placed.set(move, { u: nextU, y: nextY })
      changed = true
    }
    if (!changed) return null
    return { segment: { preset: segment.preset, gain: segment.gain, knots }, placed }
  }

  if (segment.knots.length === 0) return null
  const knots = segment.knots.map((knot) => ({ u: knot.u, y: knot.y }))
  const desired = new Map<number, { u: number; y: number; move: SpeedKnotMove }>()
  for (const move of moves) {
    if (move.knot < 0 || move.knot >= knots.length) continue
    const current = knots[move.knot]
    const y = clampSpeedY(move.y)
    if (isSpeedEdgeU(current.u)) {
      desired.set(move.knot, { u: current.u < 0.5 ? 0 : 1, y, move })
    } else {
      desired.set(move.knot, {
        u: clamp(clamp01(move.u), SPEED_EDGE_GAP, 1 - SPEED_EDGE_GAP),
        y,
        move,
      })
    }
  }
  if (desired.size === 0) return null

  const moving = new Set(desired.keys())
  const bounds = new Map<number, { lo: number; hi: number }>()
  for (const index of moving) {
    const current = knots[index]
    const want = desired.get(index)
    if (!want || isSpeedEdgeU(current.u)) continue
    let lo = SPEED_EDGE_GAP
    let hi = 1 - SPEED_EDGE_GAP
    for (let i = 0; i < knots.length; i += 1) {
      if (i === index || moving.has(i)) continue
      if (knots[i].u <= current.u) lo = Math.max(lo, knots[i].u + SPEED_NEIGHBOR_GAP)
      else hi = Math.min(hi, knots[i].u - SPEED_NEIGHBOR_GAP)
    }
    if (lo > hi) {
      want.u = current.u
      bounds.set(index, { lo: current.u, hi: current.u })
    } else {
      want.u = clamp(want.u, lo, hi)
      bounds.set(index, { lo, hi })
    }
  }

  const interiors = [...moving].filter((index) => !isSpeedEdgeU(knots[index].u)).sort((a, b) => knots[a].u - knots[b].u)
  let prev = SPEED_EDGE_GAP - SPEED_NEIGHBOR_GAP
  for (const index of interiors) {
    const want = desired.get(index)
    const limit = bounds.get(index)
    if (!want || !limit) continue
    want.u = clamp(Math.max(want.u, prev + SPEED_NEIGHBOR_GAP), limit.lo, limit.hi)
    prev = want.u
  }
  let nextU = 1 - SPEED_EDGE_GAP + SPEED_NEIGHBOR_GAP
  for (let i = interiors.length - 1; i >= 0; i -= 1) {
    const index = interiors[i]
    const want = desired.get(index)
    const limit = bounds.get(index)
    if (!want || !limit) continue
    want.u = clamp(Math.min(want.u, nextU - SPEED_NEIGHBOR_GAP), limit.lo, limit.hi)
    nextU = want.u
  }

  const written = knots.map((knot, index) => {
    const want = desired.get(index)
    return want ? { u: want.u, y: want.y } : { u: knot.u, y: knot.y }
  })
  for (const index of moving) {
    const want = desired.get(index)
    if (!want) continue
    placed.set(want.move, { u: want.u, y: want.y })
  }
  return { segment: { preset: "custom", gain: 1, knots: ensureEdgeKnots(written) }, placed }
}

export function removeSpeedKnot(
  profile: CameraSpeedProfile,
  segmentIndex: number,
  knotIndex: number,
): CameraSpeedProfile | null {
  const segment = profile.segments[segmentIndex]
  if (!segment || segment.preset !== "custom") return null
  if (knotIndex < 0 || knotIndex >= segment.knots.length) return null
  if (isSpeedEdgeU(segment.knots[knotIndex].u)) return null
  const knots = ensureEdgeKnots(segment.knots.filter((_, index) => index !== knotIndex))
  return replaceSegment(profile, segmentIndex, { preset: "custom", gain: segment.gain, knots })
}

export function scaleSpeedProfile(
  profile: CameraSpeedProfile,
  fromMs: number,
  toMs: number,
): CameraSpeedProfile | null {
  if (!(toMs > 0)) return null
  const scale = fromMs > 1e-6 ? toMs / fromMs : 1
  return normalizeSpeedProfile(
    { cuts: profile.cuts.map((cut) => cut * scale), segments: profile.segments.map(cloneSpeedSegment) },
    toMs,
  )
}

/** Оставляет кусок [fromMs, toMs] и сдвигает его к нулю. */
export function trimSpeedProfile(
  profile: CameraSpeedProfile,
  durationMs: number,
  fromMs: number,
  toMs: number,
): CameraSpeedProfile | null {
  const duration = Math.max(0, durationMs)
  const from = clamp(fromMs, 0, duration)
  const to = clamp(toMs, from, duration)
  const span = to - from
  if (!(span > 0)) return null
  const stats = speedSectionStats(profile, duration)
  const segments: SpeedSegment[] = []
  const cuts: number[] = []
  for (const section of stats) {
    const a = Math.max(section.t0, from)
    const b = Math.min(section.t1, to)
    if (b - a <= 1e-3) continue
    const dt = section.t1 - section.t0
    const u0 = dt <= 1e-6 ? 0 : (a - section.t0) / dt
    const u1 = dt <= 1e-6 ? 1 : (b - section.t0) / dt
    const partial = u0 > 1e-4 || u1 < 1 - 1e-4
    segments.push(partial ? resample(section.segment, u0, u1) : cloneSpeedSegment(section.segment))
    if (segments.length > 1) cuts.push(Math.round(a - from))
  }
  if (segments.length === 0) return null
  return repairSpeedProfile({ cuts, segments }, span)
}
